<template>
  <sc-render :fields="fields" :empty-message="$t('NO_DATA_SOURCE_EXIST')">
    <div class="container" :class="[themeClass, imagePositionClass]">
      <component
        :is="componentType"
        class="image-block"
        :field="ctaField"
      >
        <sc-image v-if="isEditMode" :media="imageField" />
        <img v-else-if="showImage" :src="imageSrc" :alt="imageAlt" />
      </component>
      <component
        :is="componentType"
        class="text-block"
        :field="ctaField"
      >
        <heading
          v-if="showTitle"
          :tagField="headingSizeField"
          desktopSize="size-2"
          mobileSize="size-5"
          class="title"
          :textField="titleField"
        />
        <sc-text v-if="showTag" tag="p" class="tag" :field="tagField" />
        <sh-button
          v-else-if="showCta"
          :[btnLinkKey]="ctaHref"
          :color="buttonColor"
          :[setStyle]="true"
        >
          {{ ctaText }}
        </sh-button>
        <p v-else-if="showCtaText" class="link">{{ ctaText }}</p>
      </component>
    </div>
  </sc-render>
</template>

<script>
  import { createNamespacedHelpers } from 'vuex'
  import { Image, Link, Text } from '@sitecore-jss/sitecore-jss-vue'
  import get from '~/helpers/get'
  import mapFields from '~/helpers/map-fields'
  import { getButtonType } from '~/helpers/get-button-type'
  import getButtonStyle from '~/helpers/get-button-style'
  import CTA from '~/constants/appearance'
  import ScRender from '../sc-render'
  import Heading from '../heading'

  const { mapGetters: mapJssGetters } = createNamespacedHelpers('jss')
  const { mapGetters: mapEnvGetters } = createNamespacedHelpers('ui/environment')

  export default {
    name: 'EditorialPortraitImage',
    props: {
      fields: {
        type: Object,
        default: () => ({})
      }
    },
    components: {
      ScRender,
      ScImage: Image,
      ScText: Text,
      ScLink: Link,
      Heading
    },
    computed: {
      ...mapJssGetters(['isEditMode']),
      ...mapEnvGetters(['isDarkTheme']),
      ...mapFields([
        'headingSize'
      ]),
      themeClass () {
        return this.isDarkTheme && 'theme-black'
      },
      imageField () {
        return get(this.fields, 'image', false)
      },
      imageSrc () {
        return get(this.imageField, 'value.cloudinary_3x4_fill_crop', '') || get(this.imageField, 'value.src', '')
      },
      imageAlt () {
        return get(this.imageField, 'value.alt', '')
      },
      imagePosition () {
        return get(this.fields, 'imagePosition.value', 'left')
      },
      imagePositionClass () {
        return this.imagePosition === 'right' ? 'image-right' : 'image-left'
      },
      titleField () {
        return get(this.fields, 'title', false)
      },
      ctaField () {
        return get(this.fields, 'cta.value', false)
      },
      ctaHref () {
        return get(this.ctaField, 'href', false)
      },
      ctaText () {
        return get(this.ctaField, 'text', '')
      },
      tagField () {
        return get(this.fields, 'tag', false)
      },
      btnLinkKey () {
        return getButtonType(this.ctaHref)
      },
      ctaAppearance () {
        return get(this.fields, 'ctaAppearance.value', '')
      },
      showImage () {
        return !this.isEditMode || !!this.imageSrc
      },
      showTitle () {
        return this.isEditMode || this.titleField
      },
      showTag () {
        if (!this.ctaAppearance) {
          return this.isEditMode || this.tagField
        }
        return false
      },
      showCta () {
        return this.ctaAppearance && this.ctaHref
      },
      buttonColor () {
        return this.isDarkTheme ? CTA.SECONDARY : CTA.PRIMARY
      },
      setStyle () {
        return getButtonStyle(this.ctaAppearance)
      },
      showCtaText () {
        return !this.isEditMode && this.ctaAppearance
      },
      componentType () {
        if (!this.isEditMode && this.ctaHref) {
          return 'sc-link'
        }
        return 'div'
      }
    }
  }
</script>

<style lang="scss" scoped>
  $full-width: 100%;
  $half-width: $full-width / 2;

  .container {
    display: flex;
    flex-direction: column;
    @include stack-space($space-090);
    @include tablet {
      flex-direction: row;
      flex: 0 0 $full-width;
      @include stack-space($space-100);
      &.image-right {
        flex-direction: row-reverse;
      }
    }
  }
  .image-block {
    flex: 0 0 $half-width;
    ::v-deep img {
      display: block;
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
  }
  .text-block {
    flex: 0 0 $half-width;
    background-color: transparent;
    text-decoration: none;
    @include inset-space($space-050);
    padding-bottom: 0;
    @include tablet {
      padding: $space-090 $space-080;
      background-color: $color-cream;
    }
  }
  .title {
    color: $color-black;
    @include stack-space($space-020);
    @include tablet {
      @include stack-space($space-050);
    }
  }
  .tag {
    @include font('040');
    text-decoration: none;
    @include stack-space(0);
    @include tablet {
      color: var(--text-color);
    }
  }
  .tag {
    text-transform: uppercase;
  }
</style>
